// WebhookModal — lifted out of the LiveTrading component body (God 2026-08-26,
// phase 4). Per-bot webhook URL + payload, copyable.
//
// Phases 1-3 moved top-level declarations, which close over nothing. This block
// lived INSIDE the component and closed over its state, so it could not be moved
// verbatim — it needed a prop for every name it borrowed. That prop list was
// computed by intersecting the block's identifiers with the component's scope
// (tools/refactor_livetrading_phase4.py), not written by hand: a hand-listed
// prop set is exactly where a missed dependency becomes a runtime undefined that
// the compiler never sees.
//
// Every prop keeps its ORIGINAL LOCAL NAME, so the JSX below is the original
// text. Behaviour is unchanged by construction.

import React from 'react';
import { Copy } from 'lucide-react';
import { Sheet } from '../components/ui/Sheet';
import { WEBHOOK_BASE_URL } from './foundation';

export interface WebhookModalProps {
  deployments: any;
  selectedBotForWebhook: any;
  setDeployments: any;
  setShowWebhookModal: any;
  showToast: any;
  showWebhookModal: any;
  testWebhook: any;
  tvToken: any;
}

export const WebhookModal: React.FC<WebhookModalProps> = ({ deployments, selectedBotForWebhook, setDeployments, setShowWebhookModal, showToast, showWebhookModal, testWebhook, tvToken }) => (
  <>
        {/* WEBHOOK MODAL */}
        {showWebhookModal && deployments.find(d => d.id === selectedBotForWebhook) && (() => {
          const bot = deployments.find(d => d.id === selectedBotForWebhook)!;
          return (
          /* Bottom sheet on a phone, centred modal on desktop — the Sheet
             primitive owns the frame, the scroll container, the safe-area
             inset and the close affordance, so this file is only its
             CONTENT now (blueprint Phase 6). */
          <Sheet
            open
            onClose={() => setShowWebhookModal(false)}
            width={600}
            title={<span style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              📡 TradingView Webhook
              <span className="jc-chip" style={{color:'#c084fc',borderColor:'rgba(168,85,247,.35)',background:'rgba(168,85,247,.12)'}}>v6.0 Smart Auto-Pricer</span>
            </span>}
          >

              <div style={{background:'#0d1117',padding:16,borderRadius:8,border:'1px solid #10b981',marginBottom:12}}>
                <div style={{fontSize:9,color:'#10b981',marginBottom:6,fontWeight:800,letterSpacing:1}}>🌐 PRODUCTION WEBHOOK URL</div>
                <input readOnly value={WEBHOOK_BASE_URL} className="lt-inp" style={{fontFamily:'monospace',color:'#10b981',marginBottom:10,background:'#020617'}}/>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>{navigator.clipboard.writeText(WEBHOOK_BASE_URL); showToast('✅ URL Copied!');}} className="lt-btn" style={{flex:1,padding:10,background:'#4f46e5',color:'#fff',fontSize:10}}>
                    <Copy size={12}/> COPY URL
                  </button>
                  <button onClick={()=>testWebhook(bot.id)} className="lt-btn" style={{flex:1,padding:10,background:'linear-gradient(135deg,#0891b2,#0e7490)',color:'#fff',fontSize:10}}>
                    🧪 TEST WEBHOOK
                  </button>
                </div>
              </div>

              <div style={{background:bot.autoFireEnabled?'rgba(16,185,129,.08)':'rgba(239,68,68,.08)',padding:14,borderRadius:8,border:`1px solid ${bot.autoFireEnabled?'#10b981':'#ef4444'}`,marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontSize:11,fontWeight:800,color:bot.autoFireEnabled?'#10b981':'#ef4444'}}>
                    🔥 Auto-Fire Engine: {bot.autoFireEnabled ? 'ENABLED' : 'DISABLED'}
                  </div>
                  <div style={{fontSize:9,color:'#94a3b8',marginTop:4}}>
                    {bot.autoFireEnabled ? 'Signals auto-execute. Backend resolves real CE/PE LTP from Dhan option chain.' : 'Signals logged only.'}
                  </div>
                </div>
                <button onClick={()=>{
                  setDeployments(p => p.map(d => d.id === bot.id ? {...d, autoFireEnabled: !d.autoFireEnabled} : d));
                  showToast(bot.autoFireEnabled ? '🛑 Auto-Fire DISABLED' : '🔥 Auto-Fire ENABLED');
                }} className="lt-btn" style={{padding:'6px 12px',fontSize:9,background:bot.autoFireEnabled?'#dc2626':'#059669',color:'#fff'}}>
                  {bot.autoFireEnabled ? 'TURN OFF' : 'TURN ON'}
                </button>
              </div>

              <div style={{background:'#0d1117',padding:12,borderRadius:8,border:'1px solid #1e293b',fontSize:10}}>
                <div style={{color:'#fbbf24',fontWeight:700,marginBottom:6,display:'flex',alignItems:'center',gap:6}}>
                  📋 Alert Message JSON (TradingView)
                  <span style={{fontSize:7,padding:'1px 5px',borderRadius:3,background:'rgba(168,85,247,.15)',color:'#a855f7',fontWeight:800}}>v6.0 AUTO-PRICER</span>
                </div>
                <code style={{color:'#10b981',fontSize:10,display:'block',whiteSpace:'pre-wrap',background:'#020617',padding:10,borderRadius:6,marginBottom:8}}>{`{
    "bot_id": "${bot.id}",
    "auth_token": "${tvToken || bot.apiKey}",
    "action": "{{strategy.order.action}}",
    "comment": "{{strategy.order.comment}}",
    "symbol": "${bot.assets[0]?.baseSymbol || 'NIFTY'}",
    "price": "{{close}}",
    "option_type": "${bot.assets[0]?.optType || 'CE'}",
    "strike_type": "${bot.assets[0]?.strikeMode || 'ATM'}",
    "auto_resolve_ltp": true,
    "mode": "${bot.tradingMode.toLowerCase()}"
  }`}</code>
                <div style={{padding:'8px 10px',background:'rgba(168,85,247,.08)',border:'1px solid rgba(168,85,247,.2)',borderRadius:6,fontSize:9,color:'#cbd5e1',lineHeight:1.6,marginBottom:8}}>
                  <strong style={{color:'#a855f7'}}>🆕 v6.0 SMART AUTO-PRICER:</strong> Sirf signal bhejo, baki sab automatic:<br/>
                  ✅ Backend spot fetch karta hai (live)<br/>
                  ✅ ATM/OTM strike auto-pick (apke <code>strike_type</code> ke according)<br/>
                  ✅ Real CE/PE LTP option chain se laata hai (Dhan API)<br/>
                  ✅ Order fire hota hai with exact same price jo terminal mein dikhta hai<br/>
                  ✅ <code>comment</code> field se action detect: BUY CE / BUY PE / 13 EMA Exit / SL / Target
                </div>
                <div style={{padding:'8px 10px',background:'#020617',border:'1px solid #1e293b',borderRadius:6,fontSize:9,color:'#94a3b8',lineHeight:1.6}}>
                  <strong style={{color:'#fbbf24'}}>📜 Pine Script:</strong>
                  <pre style={{margin:'6px 0',color:'#10b981',fontSize:9,whiteSpace:'pre-wrap'}}>{`strategy.entry("BUY_CE", strategy.long, comment="BUY CE")
  strategy.entry("BUY_PE", strategy.long, comment="BUY PE")
  strategy.close_all(comment="13 EMA Exit")`}</pre>
                </div>
                <div style={{marginTop:8,fontSize:9,color:'#64748b',lineHeight:1.6}}>
                  <strong style={{color:'#fbbf24'}}>Setup Steps:</strong><br/>
                  1. TradingView pe Alert banao<br/>
                  2. Notifications → Webhook URL enable → Paste URL<br/>
                  3. Message box mein JSON paste (with <code style={{color:'#a855f7'}}>auto_resolve_ltp:true</code>)<br/>
                  4. Pine Script mein har entry/exit pe <code style={{color:'#10b981'}}>comment="..."</code> add karo<br/>
                  5. Symbol hardcode karo (NIFTY) — <code>{`{{ticker}}`}</code> mat use karo<br/>
                  6. Save → Backend automatic ATM strike + real CE/PE LTP + IST time!
                </div>
              </div>
          </Sheet>
          );
        })()}
  </>
);
